import * as THREE from 'three';
import { ENV, AIM9 } from '../core/SimConfig.js';

export const MissileState = Object.freeze({
  MOUNTED: 0,
  FIRED: 1,
  DEAD: 2,
});

// ── Reusable temp scratch (zero-alloc hot path) ──────────────────────────────
const _worldPos = new THREE.Vector3();
const _worldQuat = new THREE.Quaternion();
const _worldScale = new THREE.Vector3();
const _r = new THREE.Vector3();
const _vRel = new THREE.Vector3();
const _aimPoint = new THREE.Vector3();
const _aimDir = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _lateral = new THREE.Vector3();
const _accel = new THREE.Vector3();
const _vNorm = new THREE.Vector3();
const NEG_Z = new THREE.Vector3(0, 0, -1);

// Drag pre-factor: 0.5 * rho * Cd * A / mass
const DRAG_K = 0.5 * ENV.airDensity * AIM9.dragCd * AIM9.bodyArea / AIM9.mass;
const THRUST_ACCEL = AIM9.thrust / AIM9.mass;
const FLAME_SPIN_RATE = 30; // rad/s about flame's local Y

/**
 * One AIM-9M missile. Lives parented to a pylon node while MOUNTED, gets
 * reparented to the scene root when FIRED, integrates physics + PN guidance
 * until it hits a target, hits terrain, expires, or is destroyed.
 */
export default class AIM9Missile {
  /**
   * @param {{
   *   pylonNode: THREE.Object3D,
   *   modelMesh: THREE.Object3D,    // cloned aim-9m mesh
   *   scene: THREE.Scene,
   * }} opts
   */
  constructor({ pylonNode, modelMesh, scene }) {
    this.pylonNode = pylonNode;
    this.scene = scene;

    // Mesh starts as a child of the pylon — its world transform is fully
    // driven by the aircraft's scene graph until launch.
    //
    // The Blender pylon empties use the default arrow orientation (arrow
    // along the empty's local +Z), but the missile model is Z-forward
    // (nose along local -Z, three.js convention). Without correction the
    // mounted missile points along the pylon's local -Z = world down.
    // +90° about local X tips the nose from pylon-down to pylon-forward,
    // aligning with the arrow direction.
    this.mesh = modelMesh;
    this.mesh.position.set(0, 0, 0);
    this.mesh.rotation.set(Math.PI / 2, 0, 0);
    pylonNode.add(this.mesh);

    this.flameMesh = this.mesh.getObjectByName('AIM-9M_Flame') ?? null;
    if (this.flameMesh) this.flameMesh.visible = false;

    this.state = MissileState.MOUNTED;

    // Free-flight physics state — populated on fire().
    this.position = new THREE.Vector3();
    this.velocity = new THREE.Vector3();
    this.quaternion = new THREE.Quaternion();
    this.age = 0;

    // Target reference — whatever entity exposes `.flight.position` /
    // `.flight.velocity` and `.hitVolume` (matches EnemyHelicopter shape).
    this.target = null;

    // Set by update() before an internal kill() so WeaponSystem can spawn
    // the appropriate explosion at m.position. { isAircraftHit: boolean } | null
    this.detonation = null;

    // Smoke-trail emitter handle (assigned by WeaponSystem on fire, released
    // back to the SmokeTrailSystem when the missile dies).
    this.smokeTrailHandle = -1;
  }

  /**
   * Detach from the pylon, reparent to scene root, capture world transform
   * as initial physics state, and begin free-flight pursuit of `target`.
   *
   * @param {object} target  — entity with .flight.position/.flight.velocity
   * @param {THREE.Vector3} aircraftVelocity — initial inertial velocity
   */
  fire(target, aircraftVelocity) {
    if (this.state !== MissileState.MOUNTED) return;

    // Snapshot world transform before reparenting.
    this.mesh.updateWorldMatrix(true, false);
    this.mesh.matrixWorld.decompose(_worldPos, _worldQuat, _worldScale);

    // Reparent: scene root, with world-space position/quat preserved.
    this.pylonNode.remove(this.mesh);
    this.scene.add(this.mesh);
    this.mesh.position.copy(_worldPos);
    this.mesh.quaternion.copy(_worldQuat);

    this.position.copy(_worldPos);
    this.launchPosition = _worldPos.clone();
    this.quaternion.copy(_worldQuat);
    this.velocity.copy(aircraftVelocity);
    this.age = 0;
    this.target = target;
    this.state = MissileState.FIRED;

    if (this.flameMesh) {
      this.flameMesh.visible = true;
      this.flameMesh.rotation.y = 0;
    }
  }

  /**
   * Integrate one step of free-flight physics + PN guidance.
   * No-op while MOUNTED or DEAD.
   *
   * Returns the new world-space position/segment so WeaponSystem can run
   * shared hit detection.
   */
  update(dt) {
    if (this.state !== MissileState.FIRED) return;

    this.age += dt;
    if (this.age > AIM9.maxLifetime) {
      this.detonation = { isAircraftHit: false };
      this.kill();
      return;
    }

    // ── Guidance: lead pursuit using closing rate ──────────────────────
    // Even when target is gone, we continue ballistic + thrust → coast out.
    let steerX = 0, steerY = 0, steerZ = 0;
    const target = this.target;
    const targetAlive = target && !target.dead;

    // Suppress guidance until clear of the launching A-10 (safe-separation).
    const dx = this.position.x - this.launchPosition.x;
    const dy = this.position.y - this.launchPosition.y;
    const dz = this.position.z - this.launchPosition.z;
    const separated = (dx * dx + dy * dy + dz * dz) > (AIM9.steerArmDistance * AIM9.steerArmDistance);

    if (targetAlive && separated) {
      _r.copy(target.flight.position).sub(this.position);
      _vRel.copy(target.flight.velocity).sub(this.velocity);

      const range = _r.length();
      if (range > 0.001) {
        // Closing speed = -(r·vRel)/|r|. Positive when range is shrinking.
        const closing = -(_r.x * _vRel.x + _r.y * _vRel.y + _r.z * _vRel.z) / range;
        const tLead = range / Math.max(closing, 50);  // floor avoids div-by-zero / negative lead

        _aimPoint.copy(target.flight.velocity).multiplyScalar(tLead).add(target.flight.position);
        _aimDir.copy(_aimPoint).sub(this.position).normalize();

        const speed = this.velocity.length();
        if (speed > 1) {
          _fwd.copy(this.velocity).divideScalar(speed);
          // Lateral component of aim direction relative to body forward
          const along = _aimDir.dot(_fwd);
          _lateral.copy(_aimDir).addScaledVector(_fwd, -along);
          const latMag = _lateral.length();
          if (latMag > 1e-4) {
            _lateral.divideScalar(latMag);
            const angle = Math.atan2(latMag, along);
            // PN-style command: a = N · Vc · ω, where ω ≈ angle / dt is
            // approximated by a proportional law on angle * speed.
            let aMag = AIM9.navConstant * Math.max(closing, speed * 0.5) * angle;
            if (aMag > AIM9.maxLateralAccel) aMag = AIM9.maxLateralAccel;
            steerX = _lateral.x * aMag;
            steerY = _lateral.y * aMag;
            steerZ = _lateral.z * aMag;
          }
        }

        // ── Proximity fuse: analytic closest-point-of-approach ─────────
        // Discrete per-frame range checks tunnel at high closing speeds
        // (a 1000 m/s intercept moves ~17 m per 60 fps frame, vs a 4 m
        // fuse radius). Solve for the in-frame minimum |r(t)| analytically:
        //   r(t) = r0 + vRel·t,  argmin at t* = −(r0·vRel)/|vRel|².
        // Two timing fixes also matter:
        //   1. Target was already advanced by enemyManager.update() this
        //      frame, so back-compute its start-of-frame position.
        //   2. Measure to hitVolume.center, not the model origin — the
        //      hitbox can be offset along the fuselage in the GLB.
        const fuseCenter = target.hitVolume?.center ?? target.flight.position;
        const tVel = target.flight.velocity;
        const r0x = fuseCenter.x - tVel.x * dt - this.position.x;
        const r0y = fuseCenter.y - tVel.y * dt - this.position.y;
        const r0z = fuseCenter.z - tVel.z * dt - this.position.z;
        const vRx = tVel.x - this.velocity.x;
        const vRy = tVel.y - this.velocity.y;
        const vRz = tVel.z - this.velocity.z;
        const vR2 = vRx * vRx + vRy * vRy + vRz * vRz;
        let tStar = 0;
        if (vR2 > 1e-4) {
          tStar = -(r0x * vRx + r0y * vRy + r0z * vRz) / vR2;
          if (tStar < 0) tStar = 0;
          else if (tStar > dt) tStar = dt;
        }
        const cpaX = r0x + vRx * tStar;
        const cpaY = r0y + vRy * tStar;
        const cpaZ = r0z + vRz * tStar;
        if (cpaX * cpaX + cpaY * cpaY + cpaZ * cpaZ < AIM9.proximityFuse * AIM9.proximityFuse) {
          const detX = this.position.x + this.velocity.x * tStar;
          const detY = this.position.y + this.velocity.y * tStar;
          const detZ = this.position.z + this.velocity.z * tStar;
          target.onProjectileHit?.(detX, detY, detZ, AIM9.damage, 'aim9');
          this.position.set(detX, detY, detZ);
          this.detonation = { isAircraftHit: true };
          this.kill();
          return;
        }
      }
    }

    // ── Body forward (along velocity) ─────────────────────────────────
    const speed = this.velocity.length();
    if (speed > 0.01) {
      _vNorm.copy(this.velocity).divideScalar(speed);
    } else {
      _vNorm.set(0, 0, -1).applyQuaternion(this.quaternion);
    }

    // ── Forces ────────────────────────────────────────────────────────
    // Thrust: along body-forward while motor burning.
    const burning = this.age < AIM9.burnTime;
    _accel.set(0, 0, 0);
    if (burning) _accel.addScaledVector(_vNorm, THRUST_ACCEL);

    if (this.flameMesh) {
      if (burning) {
        this.flameMesh.rotation.y += FLAME_SPIN_RATE * dt;
      } else if (this.flameMesh.visible) {
        this.flameMesh.visible = false;
      }
    }

    // Drag: opposite velocity, scaling with v².
    if (speed > 0.01) {
      const dragMag = DRAG_K * speed * speed;
      _accel.addScaledVector(_vNorm, -dragMag);
    }

    // Gravity
    _accel.y -= ENV.gravity;

    // Steering (perpendicular to velocity)
    _accel.x += steerX;
    _accel.y += steerY;
    _accel.z += steerZ;

    // ── Semi-implicit Euler ───────────────────────────────────────────
    this.velocity.addScaledVector(_accel, dt);

    // Save previous position for swept hit tests done by WeaponSystem.
    this.prevX = this.position.x;
    this.prevY = this.position.y;
    this.prevZ = this.position.z;

    this.position.addScaledVector(this.velocity, dt);

    // ── Orient mesh: nose along velocity (model is Z-forward → -Z) ────
    if (this.velocity.lengthSq() > 1) {
      _vNorm.copy(this.velocity).normalize();
      this.quaternion.setFromUnitVectors(NEG_Z, _vNorm);
    }
    this.mesh.position.copy(this.position);
    this.mesh.quaternion.copy(this.quaternion);
  }

  /** Detach mesh and mark dead. WeaponSystem prunes DEAD missiles. */
  kill() {
    if (this.state === MissileState.DEAD) return;
    if (this.mesh.parent) this.mesh.parent.remove(this.mesh);
    this.state = MissileState.DEAD;
    this.target = null;
  }

  dispose() {
    this.kill();
  }
}
